// pdfUtils.ts — Invoice + Rate Confirmation + Driver Settlement PDF generators using jsPDF

export interface LoadData {
  id: string;
  load_number: string;
  origin: string;
  destination: string;
  customer: string;
  // Extended fields from App.tsx schema
  customer_name?: string;
  pickup_city?: string;
  pickup_state?: string;
  delivery_city?: string;
  delivery_state?: string;
  shipper_name?: string;
  shipper_address?: string;
  shipper_phone?: string;
  consignee_name?: string;
  consignee_address?: string;
  consignee_phone?: string;
  equipment_type?: string;
  commodity?: string;
  weight_lbs?: number;
  assigned_driver_id?: string;
  rate_total: number;
  fsc?: number;
  detention?: number;
  lumper?: number;
  stop_off?: number;
  tarp?: number;
  bol_number: string;
  pickup_date: string;
  delivery_date: string;
  status: string;
  loaded_miles?: number;
  deadhead_miles?: number;
  net_profit?: number;
  profit_margin_percent?: number;
  rpm?: number;
}

export interface DriverData {
  id: string;
  name: string;
  full_name?: string;
  pay_percent: number;
  pay_rate_percent?: number;
  email: string;
  phone: string;
  cdl_number?: string;
  cdl_state?: string;
}

export interface CompanyInfo {
  name: string;
  address: string;
  city_state_zip: string;
  phone: string;
  email: string;
  mc_number: string;
  dot_number: string;
}

const DEFAULT_COMPANY: CompanyInfo = {
  name: "Blacktop.ai Logistics",
  address: "1234 Freight Blvd",
  city_state_zip: "Memphis, TN 38103",
  phone: "(901) 555-0100",
  email: "dispatch@blacktop.ai",
  mc_number: "MC-123456",
  dot_number: "DOT-789012",
};

function loadTotal(load: LoadData): number {
  return (
    (load.rate_total || 0) +
    (load.fsc || 0) +
    (load.detention || 0) +
    (load.lumper || 0) +
    (load.stop_off || 0) +
    (load.tarp || 0)
  );
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

function fmtDate(d: string): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function getOrigin(load: LoadData): string {
  if (load.pickup_city && load.pickup_state) return `${load.pickup_city}, ${load.pickup_state}`;
  return load.origin || "—";
}

function getDestination(load: LoadData): string {
  if (load.delivery_city && load.delivery_state) return `${load.delivery_city}, ${load.delivery_state}`;
  return load.destination || "—";
}

function getCustomer(load: LoadData): string {
  return load.customer_name || load.customer || "—";
}

// ─── INVOICE PDF ────────────────────────────────────────────────────────────

export async function generateInvoicePDF(
  load: LoadData,
  company: CompanyInfo = DEFAULT_COMPANY
): Promise<void> {
  const jsPDF = (await import("jspdf")).default;
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  const W = 612;
  const margin = 48;
  let y = 48;

  // ── Header bar
  doc.setFillColor(20, 20, 30);
  doc.rect(0, 0, W, 90, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text(company.name.toUpperCase(), margin, 38);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(
    [company.address, company.city_state_zip, company.phone, company.email],
    margin,
    52
  );

  doc.setFontSize(10);
  doc.text(`${company.mc_number}  |  ${company.dot_number}`, margin, 80);

  // INVOICE label top right
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 200, 60);
  doc.text("INVOICE", W - margin, 42, { align: "right" });

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(200, 200, 200);
  doc.text(`#${load.load_number}`, W - margin, 58, { align: "right" });
  doc.text(`Date: ${fmtDate(new Date().toISOString())}`, W - margin, 72, {
    align: "right",
  });

  y = 110;

  // ── Bill To / Load Info
  doc.setTextColor(30, 30, 40);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("BILL TO", margin, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(getCustomer(load), margin, y + 14);

  // Load details right column
  const col2 = W / 2 + 20;
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(100, 100, 120);
  const details = [
    ["BOL NUMBER", load.bol_number || "—"],
    ["PICKUP DATE", fmtDate(load.pickup_date)],
    ["DELIVERY DATE", fmtDate(load.delivery_date)],
    ["ORIGIN", getOrigin(load)],
    ["DESTINATION", getDestination(load)],
  ];
  details.forEach(([label, val], i) => {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(120, 120, 140);
    doc.text(label, col2, y + i * 16);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 30, 40);
    doc.text(val, col2 + 110, y + i * 16);
  });

  y += 100;

  // ── Divider
  doc.setDrawColor(220, 220, 230);
  doc.setLineWidth(1);
  doc.line(margin, y, W - margin, y);
  y += 18;

  // ── Line items table header
  doc.setFillColor(20, 20, 30);
  doc.rect(margin, y, W - margin * 2, 24, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("DESCRIPTION", margin + 10, y + 15);
  doc.text("AMOUNT", W - margin - 10, y + 15, { align: "right" });
  y += 24;

  const lineItems: [string, number][] = [
    ["Linehaul Rate", load.rate_total || 0],
    ["Fuel Surcharge (FSC)", load.fsc || 0],
    ["Detention", load.detention || 0],
    ["Lumper Fee", load.lumper || 0],
    ["Stop-Off Charge", load.stop_off || 0],
    ["Tarp Fee", load.tarp || 0],
  ].filter(([, v]) => (v as number) > 0) as [string, number][];

  lineItems.forEach(([desc, amt], i) => {
    const rowY = y + i * 28;
    if (i % 2 === 0) {
      doc.setFillColor(248, 248, 252);
      doc.rect(margin, rowY, W - margin * 2, 28, "F");
    }
    doc.setTextColor(30, 30, 40);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(desc, margin + 10, rowY + 18);
    doc.text(fmt(amt), W - margin - 10, rowY + 18, { align: "right" });
  });

  y += lineItems.length * 28 + 10;

  // ── Total
  const total = loadTotal(load);
  doc.setFillColor(20, 20, 30);
  doc.rect(W - margin - 200, y, 200, 36, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("TOTAL DUE", W - margin - 190, y + 14);
  doc.setTextColor(255, 200, 60);
  doc.setFontSize(14);
  doc.text(fmt(total), W - margin - 10, y + 24, { align: "right" });

  y += 60;

  // ── Footer
  doc.setDrawColor(220, 220, 230);
  doc.line(margin, y, W - margin, y);
  y += 14;
  doc.setTextColor(140, 140, 160);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(
    "Payment due Net 30. Thank you for your business.",
    W / 2,
    y,
    { align: "center" }
  );

  doc.save(`Invoice_${load.load_number}.pdf`);
}

// ─── RATE CONFIRMATION PDF ──────────────────────────────────────────────────

export async function generateRateConfirmationPDF(
  load: LoadData,
  company: CompanyInfo = DEFAULT_COMPANY
): Promise<void> {
  const jsPDF = (await import("jspdf")).default;
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  const W = 612;
  const margin = 48;
  let y = 0;

  // ── Header
  doc.setFillColor(0, 80, 200);
  doc.rect(0, 0, W, 90, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(company.name.toUpperCase(), margin, 36);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("RATE CONFIRMATION", margin, 52);
  doc.text([company.address, company.city_state_zip, company.phone, company.email], margin, 64);

  doc.setFontSize(10);
  doc.setTextColor(180, 210, 255);
  doc.text(`Load #${load.load_number}`, W - margin, 36, { align: "right" });
  doc.setFontSize(9);
  doc.setTextColor(200, 220, 255);
  doc.text(`${company.mc_number}  |  ${company.dot_number}`, W - margin, 52, { align: "right" });
  doc.text(`Date: ${fmtDate(new Date().toISOString())}`, W - margin, 66, { align: "right" });

  y = 108;

  // ── Load Route Banner
  doc.setFillColor(240, 245, 255);
  doc.rect(margin, y, W - margin * 2, 60, "F");
  doc.setDrawColor(0, 80, 200);
  doc.setLineWidth(2);
  doc.line(margin, y, margin, y + 60);

  doc.setTextColor(0, 80, 200);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("ORIGIN", margin + 12, y + 16);
  doc.setTextColor(20, 20, 30);
  doc.setFontSize(14);
  doc.text(getOrigin(load), margin + 12, y + 34);
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 120);
  if (load.shipper_name) doc.text(load.shipper_name, margin + 12, y + 50);

  const midX = W / 2;
  doc.setTextColor(0, 80, 200);
  doc.setFontSize(16);
  doc.text("→", midX, y + 34, { align: "center" });

  doc.setTextColor(0, 80, 200);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("DESTINATION", W - margin - 150, y + 16);
  doc.setTextColor(20, 20, 30);
  doc.setFontSize(14);
  doc.text(getDestination(load), W - margin - 150, y + 34);
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 120);
  if (load.consignee_name) doc.text(load.consignee_name, W - margin - 150, y + 50);

  y += 78;

  // ── Load Details Grid
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 80, 200);
  doc.text("LOAD DETAILS", margin, y);
  y += 12;
  doc.setDrawColor(0, 80, 200);
  doc.setLineWidth(0.5);
  doc.line(margin, y, W - margin, y);
  y += 10;

  const detailRows: [string, string][] = [
    ["Equipment Type", load.equipment_type || "—"],
    ["Commodity", load.commodity || "—"],
    ["Weight", load.weight_lbs ? `${load.weight_lbs.toLocaleString()} lbs` : "—"],
    ["Pickup Date", fmtDate(load.pickup_date)],
    ["Delivery Date", fmtDate(load.delivery_date)],
    ["BOL Number", load.bol_number || "—"],
    ["Customer / Broker", getCustomer(load)],
    ["Load Status", load.status || "ACTIVE"],
  ];

  detailRows.forEach(([label, val], i) => {
    const rowY = y + i * 22;
    if (i % 2 === 0) {
      doc.setFillColor(245, 248, 255);
      doc.rect(margin, rowY - 4, W - margin * 2, 22, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(100, 110, 140);
    doc.text(label, margin + 8, rowY + 10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(20, 20, 30);
    doc.text(val, margin + 180, rowY + 10);
  });

  y += detailRows.length * 22 + 20;

  // ── Rate Section
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 80, 200);
  doc.text("RATE BREAKDOWN", margin, y);
  y += 12;
  doc.line(margin, y, W - margin, y);
  y += 10;

  // Header
  doc.setFillColor(0, 80, 200);
  doc.rect(margin, y, W - margin * 2, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8.5);
  doc.text("CHARGE TYPE", margin + 8, y + 14);
  doc.text("AMOUNT", W - margin - 8, y + 14, { align: "right" });
  y += 22;

  const rateItems: [string, number][] = [
    ["Linehaul Rate", load.rate_total || 0],
    ["Fuel Surcharge (FSC)", load.fsc || 0],
    ["Detention", load.detention || 0],
    ["Lumper Fee", load.lumper || 0],
    ["Stop-Off Charge", load.stop_off || 0],
    ["Tarp Fee", load.tarp || 0],
  ].filter(([, v]) => (v as number) > 0) as [string, number][];

  rateItems.forEach(([desc, amt], i) => {
    const rowY = y + i * 26;
    if (i % 2 === 0) {
      doc.setFillColor(245, 248, 255);
      doc.rect(margin, rowY, W - margin * 2, 26, "F");
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 40);
    doc.text(desc, margin + 8, rowY + 17);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 80, 200);
    doc.text(fmt(amt), W - margin - 8, rowY + 17, { align: "right" });
  });

  y += rateItems.length * 26 + 12;

  // Total box
  const total = loadTotal(load);
  doc.setFillColor(0, 80, 200);
  doc.roundedRect(W - margin - 220, y, 220, 40, 6, 6, "F");
  doc.setTextColor(180, 210, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("TOTAL RATE", W - margin - 210, y + 15);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text(fmt(total), W - margin - 8, y + 30, { align: "right" });

  y += 60;

  // ── Shipper / Consignee Details
  if (load.shipper_name || load.consignee_name) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 80, 200);
    doc.text("PICKUP / DELIVERY CONTACTS", margin, y);
    y += 12;
    doc.setLineWidth(0.5);
    doc.line(margin, y, W - margin, y);
    y += 10;

    const half = (W - margin * 2) / 2 - 10;
    // Shipper box
    doc.setFillColor(245, 248, 255);
    doc.rect(margin, y, half, 70, "F");
    doc.setTextColor(0, 80, 200);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("SHIPPER / PICKUP", margin + 8, y + 14);
    doc.setTextColor(20, 20, 30);
    doc.setFontSize(10);
    doc.text(load.shipper_name || "—", margin + 8, y + 28);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 100);
    if (load.shipper_address) doc.text(load.shipper_address, margin + 8, y + 42);
    if (load.shipper_phone) doc.text(load.shipper_phone, margin + 8, y + 56);

    // Consignee box
    const col2x = margin + half + 20;
    doc.setFillColor(245, 248, 255);
    doc.rect(col2x, y, half, 70, "F");
    doc.setTextColor(0, 80, 200);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("CONSIGNEE / DELIVERY", col2x + 8, y + 14);
    doc.setTextColor(20, 20, 30);
    doc.setFontSize(10);
    doc.text(load.consignee_name || "—", col2x + 8, y + 28);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 100);
    if (load.consignee_address) doc.text(load.consignee_address, col2x + 8, y + 42);
    if (load.consignee_phone) doc.text(load.consignee_phone, col2x + 8, y + 56);

    y += 90;
  }

  // ── Signature Line
  doc.setDrawColor(180, 180, 200);
  doc.setLineWidth(0.5);
  doc.line(margin, y, W / 2 - 20, y);
  doc.line(W / 2 + 20, y, W - margin, y);
  y += 14;
  doc.setTextColor(140, 140, 160);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("Carrier Signature / Date", margin, y);
  doc.text("Dispatcher Signature / Date", W / 2 + 20, y);

  y += 24;

  // ── Footer
  doc.setDrawColor(0, 80, 200);
  doc.setLineWidth(1);
  doc.line(margin, y, W - margin, y);
  y += 12;
  doc.setTextColor(100, 120, 160);
  doc.setFontSize(8);
  doc.text(
    `This rate confirmation is subject to the terms and conditions on file. ${company.mc_number} | ${company.dot_number}`,
    W / 2,
    y,
    { align: "center" }
  );

  doc.save(`RateConf_${load.load_number}.pdf`);
}

// ─── DRIVER SETTLEMENT PDF ──────────────────────────────────────────────────

export async function generateSettlementPDF(
  load: LoadData,
  driver: DriverData,
  company: CompanyInfo = DEFAULT_COMPANY
): Promise<void> {
  const jsPDF = (await import("jspdf")).default;
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  const W = 612;
  const margin = 48;
  let y = 0;

  const total = loadTotal(load);
  const driverName = driver.full_name || driver.name || "Driver";
  const payPct = driver.pay_rate_percent ?? driver.pay_percent ?? 0;
  const driverPay = total * (payPct / 100);

  // ── Header
  doc.setFillColor(14, 100, 60);
  doc.rect(0, 0, W, 90, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(company.name.toUpperCase(), margin, 36);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("DRIVER SETTLEMENT STATEMENT", margin, 52);
  doc.text(
    [company.address, company.city_state_zip, company.phone],
    margin,
    65
  );

  doc.setFontSize(10);
  doc.setTextColor(180, 255, 200);
  doc.text(`Load #${load.load_number}`, W - margin, 40, { align: "right" });
  doc.setFontSize(9);
  doc.setTextColor(200, 240, 220);
  doc.text(
    `Statement Date: ${fmtDate(new Date().toISOString())}`,
    W - margin,
    56,
    { align: "right" }
  );

  y = 108;

  // ── Driver Info box
  doc.setFillColor(240, 250, 244);
  doc.rect(margin, y, W - margin * 2, 70, "F");
  doc.setDrawColor(14, 100, 60);
  doc.setLineWidth(2);
  doc.line(margin, y, margin, y + 70);

  doc.setTextColor(14, 100, 60);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("DRIVER", margin + 12, y + 16);

  doc.setTextColor(20, 20, 30);
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text(driverName, margin + 12, y + 32);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 100);
  if (driver.email) doc.text(driver.email, margin + 12, y + 46);
  if (driver.phone) doc.text(driver.phone, margin + 12, y + 59);
  if (driver.cdl_number) {
    doc.text(`CDL: ${driver.cdl_number}${driver.cdl_state ? ` (${driver.cdl_state})` : ""}`, W / 2, y + 46);
  }

  // Pay rate badge
  doc.setFillColor(14, 100, 60);
  doc.roundedRect(W - margin - 100, y + 12, 100, 46, 6, 6, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("PAY RATE", W - margin - 50, y + 26, { align: "center" });
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(`${payPct}%`, W - margin - 50, y + 48, {
    align: "center",
  });

  y += 90;

  // ── Trip Details
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(14, 100, 60);
  doc.text("TRIP DETAILS", margin, y);
  y += 12;

  doc.setDrawColor(14, 100, 60);
  doc.setLineWidth(0.5);
  doc.line(margin, y, W - margin, y);
  y += 10;

  const tripRows: [string, string][] = [
    ["Origin", getOrigin(load)],
    ["Destination", getDestination(load)],
    ["Pickup Date", fmtDate(load.pickup_date)],
    ["Delivery Date", fmtDate(load.delivery_date)],
    ["BOL Number", load.bol_number || "—"],
    ["Customer", getCustomer(load)],
    ["Equipment", load.equipment_type || "—"],
    ["Loaded Miles", load.loaded_miles ? `${load.loaded_miles.toLocaleString()} mi` : "—"],
  ];

  tripRows.forEach(([label, val], i) => {
    const rowY = y + i * 22;
    if (i % 2 === 0) {
      doc.setFillColor(248, 252, 249);
      doc.rect(margin, rowY - 4, W - margin * 2, 22, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(100, 120, 110);
    doc.text(label, margin + 8, rowY + 10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(20, 20, 30);
    doc.text(val, margin + 150, rowY + 10);
  });

  y += tripRows.length * 22 + 20;

  // ── Rate breakdown
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(14, 100, 60);
  doc.text("RATE BREAKDOWN", margin, y);
  y += 12;
  doc.line(margin, y, W - margin, y);
  y += 10;

  // Header
  doc.setFillColor(20, 20, 30);
  doc.rect(margin, y, W - margin * 2, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8.5);
  doc.text("CHARGE TYPE", margin + 8, y + 14);
  doc.text("GROSS", W - margin - 110, y + 14, { align: "right" });
  doc.text(`DRIVER (${payPct}%)`, W - margin - 8, y + 14, {
    align: "right",
  });
  y += 22;

  const charges: [string, number][] = [
    ["Linehaul Rate", load.rate_total || 0],
    ["Fuel Surcharge (FSC)", load.fsc || 0],
    ["Detention", load.detention || 0],
    ["Lumper Fee", load.lumper || 0],
    ["Stop-Off Charge", load.stop_off || 0],
    ["Tarp Fee", load.tarp || 0],
  ].filter(([, v]) => (v as number) > 0) as [string, number][];

  charges.forEach(([desc, amt], i) => {
    const rowY = y + i * 26;
    if (i % 2 === 0) {
      doc.setFillColor(248, 252, 249);
      doc.rect(margin, rowY, W - margin * 2, 26, "F");
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 40);
    doc.text(desc, margin + 8, rowY + 17);
    doc.setTextColor(60, 60, 80);
    doc.text(fmt(amt), W - margin - 110, rowY + 17, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.setTextColor(14, 100, 60);
    doc.text(
      fmt(amt * (payPct / 100)),
      W - margin - 8,
      rowY + 17,
      { align: "right" }
    );
  });

  y += charges.length * 26 + 12;

  // ── Totals
  doc.setDrawColor(14, 100, 60);
  doc.setLineWidth(1);
  doc.line(W - margin - 260, y, W - margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 80);
  doc.text("TOTAL GROSS REVENUE", W - margin - 250, y + 12);
  doc.text(fmt(total), W - margin - 110, y + 12, { align: "right" });
  y += 24;

  // Driver gross pay box
  doc.setFillColor(14, 100, 60);
  doc.roundedRect(W - margin - 260, y, 260, 40, 6, 6, "F");
  doc.setTextColor(200, 255, 220);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("DRIVER GROSS PAY", W - margin - 250, y + 15);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text(fmt(driverPay), W - margin - 8, y + 28, { align: "right" });

  y += 60;

  // ── Footer
  doc.setDrawColor(200, 220, 210);
  doc.setLineWidth(0.5);
  doc.line(margin, y, W - margin, y);
  y += 12;
  doc.setTextColor(140, 140, 160);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(
    "This settlement is based on the percentage agreement on file. Questions? Contact dispatch.",
    W / 2,
    y,
    { align: "center" }
  );

  doc.save(`Settlement_${driverName.replace(/\s+/g, "_")}_Load${load.load_number}.pdf`);
}
